import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000, // 30 second timeout
  withCredentials: true, // send/receive the bp_trust cookie for trust-this-device
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Retry logic for failed requests
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000;

const shouldRetry = (error) => {
  // Retry on network errors or 5xx server errors
  return !error.response || (error.response.status >= 500 && error.response.status < 600);
};

// Handle auth errors and retries
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Initialize retry count
    config._retryCount = config._retryCount || 0;
    
    // Retry on network/server errors (but not auth errors)
    if (shouldRetry(error) && config._retryCount < MAX_RETRIES) {
      config._retryCount += 1;
      console.warn(`API retry ${config._retryCount}/${MAX_RETRIES} for ${config.url}`);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * config._retryCount));
      
      return api(config);
    }
    
    if (error.response?.status === 401) {
      // Don't redirect if we're on the login page (during 2FA flow)
      // or if the request is for auth endpoints
      const isAuthEndpoint = error.config?.url?.includes('/auth/');
      const isOnLoginPage = window.location.pathname === '/login';
      
      if (!isAuthEndpoint && !isOnLoginPage) {
        console.warn('Session expired, redirecting to login');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    
    // Log error for debugging
    if (error.response) {
      console.error(`API Error: ${error.response.status} ${error.config?.url}`, error.response.data);
    } else if (error.request) {
      console.error(`API Network Error: ${error.config?.url}`, error.message);
    }
    
    return Promise.reject(error);
  }
);

export default api;
